import { Token } from '@frontend/authentication-v2';
import { CostCenterClient } from '@frontend/cost-center/api';
import { CostCenter, CostCenterStatus } from '@frontend/cost-center/types';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

const COST_CENTER_PAGE_SIZE = 25;

interface ViewProps {
    userCostCenters: CostCenter[];
    isCostCenterEnabled: boolean;
    isCostCenterLoading: boolean;
    shouldShowCostCenterSelect: boolean;
    hasNoCostCenters: boolean;
    selectedUserCostCenter: string | null;
    setSelectedUserCostCenter: Dispatch<SetStateAction<string | null>>;
    // eslint-disable-next-line no-unused-vars
    onCostCenterSelect: (costCenterId: string | null) => void;
    // eslint-disable-next-line no-unused-vars
    onCostCenterSearch: (value: string) => void;
    onCostCenterScrollToBottom(): void;
}

export const useCostCenterSelection = (userToken?: Token, initialSelectedCostCenterId?: string | null): ViewProps => {
    const costCenterSelectionAvailable = !!userToken;
    const [userCostCenters, setUserCostCenters] = useState<CostCenter[]>([]);
    const [selectedUserCostCenter, setSelectedUserCostCenter] = useState<string | null>(
        costCenterSelectionAvailable ? (initialSelectedCostCenterId ?? null) : null
    );
    const [isCostCenterEnabled, setIsCostCenterEnabled] = useState<boolean>(costCenterSelectionAvailable);
    const [isCostCenterLoading, setIsCostCenterLoading] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [pageIndex, setPageIndex] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const loadingRef = useRef<boolean>(false);
    const requestIdRef = useRef<number>(0);
    const selectedCostCenterRef = useRef<string | null>(costCenterSelectionAvailable ? (initialSelectedCostCenterId ?? null) : null);

    const fetchCostCenters = useCallback(
        async (index: number, searchValue: string, mode: 'replace' | 'append') => {
            if (!userToken) return;

            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;
            loadingRef.current = true;
            setIsCostCenterLoading(true);
            try {
                const response = await CostCenterClient.fetchCostCenters(
                    {
                        status: CostCenterStatus.ACTIVE,
                        index: index.toString(),
                        size: COST_CENTER_PAGE_SIZE.toString(),
                        search: searchValue || undefined
                    },
                    userToken
                );
                if (requestId !== requestIdRef.current) return;

                setTotalCount(response.count);
                setPageIndex(index);
                setUserCostCenters((current) => {
                    const next = mode === 'append' ? [...current] : [];
                    response.results.forEach((costCenter) => {
                        if (!next.some((existing) => existing.id === costCenter.id)) next.push(costCenter);
                    });
                    return next;
                });
            } finally {
                if (requestId === requestIdRef.current) {
                    loadingRef.current = false;
                    setIsCostCenterLoading(false);
                }
            }
        },
        [userToken]
    );

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearch(search), 300);
        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (!userToken) {
            requestIdRef.current += 1;
            loadingRef.current = false;
            setIsCostCenterEnabled(false);
            setIsCostCenterLoading(false);
            setUserCostCenters([]);
            selectedCostCenterRef.current = null;
            setSelectedUserCostCenter(null);
            setTotalCount(0);
            setPageIndex(0);
            return;
        }

        setIsCostCenterEnabled(true);
        fetchCostCenters(0, debouncedSearch, 'replace');
    }, [debouncedSearch, fetchCostCenters, userToken]);

    const onCostCenterSelect = (costCenterId: string | null) => {
        selectedCostCenterRef.current = costCenterId;
        setSelectedUserCostCenter(costCenterId);
        setSearch('');
        setDebouncedSearch('');
    };

    const onCostCenterSearch = (value: string) => {
        setSearch(value);
    };

    const onCostCenterScrollToBottom = () => {
        if (!isCostCenterEnabled || loadingRef.current) return;
        if (userCostCenters.length >= totalCount) return;
        fetchCostCenters(pageIndex + 1, debouncedSearch, 'append');
    };

    return {
        userCostCenters,
        isCostCenterEnabled,
        isCostCenterLoading,
        shouldShowCostCenterSelect: isCostCenterEnabled && (totalCount > 1 || search.length > 0),
        hasNoCostCenters: isCostCenterEnabled && !isCostCenterLoading && userCostCenters.length === 0 && search.length === 0,
        selectedUserCostCenter,
        setSelectedUserCostCenter,
        onCostCenterSelect,
        onCostCenterSearch,
        onCostCenterScrollToBottom
    };
};
